import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const Proprietorship = () => {
  const data = {
    title: 'Proprietorship Registration',
    description: 'Start your business quickly as a sole proprietor with minimal formalities. The simplest and most economical way to begin your entrepreneurial journey.',
    keywords: 'proprietorship registration, sole proprietorship, business registration, GST registration, shop license, small business India',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Sole Proprietorship is the oldest and simplest form of business structure in India, owned and managed by a 
          single individual. There is no legal distinction between the owner and the business – they are one and the same. 
          This makes it the easiest business to start, with minimal paperwork, low cost, and complete freedom in decision-making.
        </p>
        <p>
          <strong>Why Choose Proprietorship?</strong> Proprietorship is ideal for small businesses, freelancers, consultants, 
          local shops, home-based businesses, and anyone starting with limited capital. It requires no formal registration 
          with MCA, and you can begin operations almost immediately with just a few basic licenses depending on your business type.
        </p>
        <p>
          <strong>Legal Framework:</strong> Unlike companies and LLPs, there is no specific act governing proprietorships. 
          The business is identified by the proprietor's PAN. However, you need to register under the Shops and Establishments 
          Act (state-specific), obtain GST registration if turnover exceeds threshold (₹20/40 lakhs), and comply with 
          income tax regulations.
        </p>
        <p>
          <strong>Important Consideration:</strong> Proprietorship has unlimited liability – your personal assets can be 
          used to settle business debts. This is the main disadvantage compared to LLP or Private Limited Company. However, 
          for low-risk businesses and beginners, the simplicity often outweighs this concern.
        </p>
      </div>
    ),
    
    benefits: [
      'Easiest and quickest business structure to set up – start within days',
      'Lowest cost of formation – no registration fees with MCA',
      'Complete control over all business decisions – no partners or board',
      'Minimal compliance requirements – no annual filings with MCA',
      'All profits belong to the proprietor – no sharing or dividend tax',
      'Tax benefits – business income taxed as personal income with deductions',
      'Easy to close/dissolve the business – no lengthy legal process',
      'Full privacy – no public disclosure of business details',
      'Can convert to LLP or Company later as business grows',
      'Bank accounts can be opened as "proprietorship concern"',
      'Suitable for freelancers, consultants, and service providers',
      'No minimum capital requirement – start with any amount',
    ],
    
    documents: [
      'PAN Card of the proprietor (business PAN is same as personal PAN)',
      'Aadhaar Card of the proprietor',
      'Passport size photograph (recent, colored)',
      'Address proof of business place: Rent agreement or Ownership documents',
      'Bank account statement or passbook (with proprietor name)',
      'Utility bill of business premises (electricity/water bill)',
      'NOC from landlord (if rented premises)',
      'Business logo/letterhead (for branding purposes)',
      'Mobile number linked with Aadhaar (for OTP verification)',
      'Email ID for official communications',
    ],
    
    process: [
      'Business name selection (can be proprietor name or trade name)',
      'GST registration (if required based on turnover or business type)',
      'Shop and Establishment license from local municipal authority',
      'MSME/Udyam registration for government benefits',
      'Opening current bank account in business/trade name',
      'Professional Tax registration (in applicable states)',
      'Other licenses as per business type (FSSAI, trade license, etc.)',
      'Business insurance (optional but recommended)',
      'Digital payment setup (UPI, payment gateway)',
      'Accounting system setup for tax compliance',
    ],
    
    timeline: '5-7 Working Days',
    pricing: '₹3,999',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>What's Included in Our Package:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>GST Registration (if applicable)</li>
          <li>Shop & Establishment License application</li>
          <li>MSME/Udyam Registration</li>
          <li>Business name guidance and selection</li>
          <li>Bank account opening assistance</li>
          <li>Basic accounting setup consultation</li>
        </ul>
        <p className="mt-3"><strong>Annual Compliance:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Income Tax Return (ITR-3 or ITR-4) by 31st July</li>
          <li>GST Returns (if registered): GSTR-1, GSTR-3B monthly/quarterly</li>
          <li>Shop License renewal (annually in most states)</li>
          <li>TDS filing if applicable</li>
          <li>Tax Audit if turnover exceeds ₹1 crore (or ₹10 crore with digital transactions)</li>
        </ul>
        <p className="mt-3"><strong>GST Registration Requirement:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Mandatory if turnover &gt; ₹20 lakhs (₹10 lakhs for special states)</li>
          <li>Mandatory for e-commerce sellers (no threshold)</li>
          <li>Mandatory for inter-state supply (no threshold)</li>
          <li>Optional for local businesses below threshold</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'Is registration mandatory for proprietorship?',
        answer: 'Proprietorship registration is not mandatory with any central authority. However, GST registration may be required based on turnover (>₹20 lakhs) or business type. Shop & Establishment license is required in most states for commercial premises.',
      },
      {
        question: 'What is the liability in proprietorship?',
        answer: 'Proprietorship has unlimited liability, meaning the proprietor\'s personal assets (house, car, savings) can be used to settle business debts. This is the main risk compared to LLP or Company structures.',
      },
      {
        question: 'Can I have employees in proprietorship?',
        answer: 'Yes, proprietorship can hire unlimited employees. You need to comply with labor laws, PF/ESI registration (if employees exceed threshold), and TDS on salaries. Employment terms are decided by the proprietor.',
      },
      {
        question: 'How is proprietorship taxed?',
        answer: 'Proprietorship income is taxed as personal income of the proprietor under Income Tax slab rates. Business expenses are deductible. ITR-3 (regular) or ITR-4 (presumptive taxation) is filed based on accounting method.',
      },
      {
        question: 'Can I open a bank account in business name?',
        answer: 'Yes, you can open a current account in your trade/business name as "proprietorship concern." Banks require GST certificate, Shop license, or MSME registration as proof of business for opening such accounts.',
      },
      {
        question: 'What is the difference between proprietorship and OPC?',
        answer: 'Proprietorship has unlimited liability and is not a separate legal entity. OPC (One Person Company) has limited liability, is a separate legal entity, requires MCA registration, and has more compliance. Proprietorship is simpler but riskier.',
      },
      {
        question: 'Can proprietorship be converted to company?',
        answer: 'Proprietorship cannot be directly "converted" but can be "taken over" by a new company. You register a new Private Limited Company and transfer the business assets, liabilities, and operations to it. The proprietorship ceases to exist.',
      },
      {
        question: 'What happens to proprietorship if owner dies?',
        answer: 'Proprietorship ceases to exist with the death of the proprietor. Business assets become part of personal estate and pass to legal heirs. Heirs can start a new proprietorship or company to continue the business.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default Proprietorship;
